import { useEffect, useState, useCallback } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  getSaleInvoice,
  fetchSaleInvoices,
  generateInvoiceNumber,
} from "../../../../../Redux/saleInvoiceSlice";
import { fetchProducts } from "../../../../../Redux/productsSlice";
import { fetchClients } from "../../../../../Redux/clientsSlice";
import { fetchDrivers } from "../../../../../Redux/driversSlice";
import { fetchCities } from "../../../../../Redux/citiesSlice";

export const notify = (type, msg) => {
  toast(
    <strong>
      <i
        className={`fas ${
          type === 1 ? "fa-check-circle" : "fa-exclamation-circle"
        }`}
      ></i>
      {msg}
    </strong>,
    { type: type === 1 ? toast.TYPE.SUCCESS : toast.TYPE.ERROR },
  );
};

export const useInvoiceData = (invoiceType, isUpdate = false) => {
  const dispatch = useDispatch();
  const navigate = useHistory();
  const location = useLocation();
  const { id: paramId } = useParams();

  const id = !isNaN(paramId) ? paramId : null;

  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const day = String(currentDate.getDate()).padStart(2, "0");
  const dateString = `${year}-${month}-${day}`;

  // State
  const [date, setDate] = useState(dateString);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [client, setClient] = useState(null);
  const [driver, setDriver] = useState(null);
  const [totalHT, setTotalHT] = useState(0);
  const [totalTTC, setTotalTTC] = useState(0);
  const [totalVAT, setTotalVAT] = useState(0);
  const [type, setType] = useState(invoiceType);
  const [status, setStatus] = useState("DRAFT");
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [shippingNoteInvoices, setShippingNoteInvoices] = useState([]);
  const [selectedShippingNote, setSelectedShippingNote] = useState(null);
  const [shippingNoteProducts, setShippingNoteProducts] = useState([]);
  const [loadingDeliveryNotes, setLoadingDeliveryNotes] = useState(false);
  const [loadingShippingNotes, setLoadingShippingNotes] = useState(false);
  const [originalShippingNoteId, setOriginalShippingNoteId] = useState(null);
  const [invoice, setInvoice] = useState(null);

  // Auto-generation states
  const [generatingNumber, setGeneratingNumber] = useState(false);

  const calculateTotals = useCallback(() => {
    let ht = 0;
    let vat = 0;
    let ttc = 0;

    invoiceItems.forEach((item) => {
      const itemHT = (item.price || 0) * (item.quantity || 0);
      const itemVAT = itemHT * ((item.vatRate || 0) / 100);
      const itemTTC = itemHT + itemVAT;

      ht += itemHT;
      vat += itemVAT;
      ttc += itemTTC;
    });

    setTotalHT(ht);
    setTotalVAT(vat);
    setTotalTTC(ttc);
  }, [invoiceItems]);

  // Auto-generate invoice number function (only for add mode)
  const autoGenerateNumber = useCallback(async () => {
    if (isUpdate) {
      // Don't auto-generate for update mode
      return;
    }

    setGeneratingNumber(true);
    try {
      const result = await dispatch(generateInvoiceNumber(type)).unwrap();
      if (result && result.nextInvoiceNumber) {
        setInvoiceNumber(result.nextInvoiceNumber);
        notify(1, "Numéro de facture généré automatiquement");
      } else {
        notify(2, "Erreur lors de la génération du numéro");
      }
    } catch (error) {
      console.error("Error generating invoice number:", error);
      notify(2, "Erreur lors de la génération du numéro");
    } finally {
      setGeneratingNumber(false);
    }
  }, [dispatch, type, isUpdate]);

  // Fetch master data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientRes, productRes, driverRes, citiesRes] = await Promise.all(
          [
            dispatch(fetchClients()),
            dispatch(fetchProducts()),
            dispatch(fetchDrivers()),
            dispatch(fetchCities()),
          ],
        );
        setClients(clientRes.payload || []);
        setProducts(productRes.payload || []);
        setDrivers(driverRes.payload || []);
        setCities(citiesRes.payload || []);
      } catch (error) {
        console.error("Error fetching master data:", error);
        notify(2, "Erreur lors du chargement des données");
      }
    };
    fetchData();
  }, [dispatch]);

  // Auto-generate number only on component mount for add mode
  useEffect(() => {
    if (!isUpdate && !invoiceNumber) {
      autoGenerateNumber();
    }
  }, [isUpdate, autoGenerateNumber, invoiceNumber]);

  // Fetch invoice for update
  useEffect(() => {
    if (isUpdate && id) {
      const fetchInvoice = async () => {
        try {
          const response = await dispatch(getSaleInvoice(id));
          const data = response.payload;

          // Store full invoice data
          setInvoice(data);

          setInvoiceNumber(data.invoiceNumber);
          setDate(data.date.split("T")[0]);
          setStartDate(data.startDate ? data.startDate.split("T")[0] : "");
          setEndDate(data.endDate ? data.endDate.split("T")[0] : "");
          setType(data.type);
          setStatus(data.status);
          setTotalHT(data.totalHT);
          setTotalTTC(data.totalTTC);

          // Set shipping note ID if exists
          if (data.shippingNoteId) {
            setOriginalShippingNoteId(data.shippingNoteId);
          }

          if (data.cities && data.cities.length > 0) {
            const citiesFromInvoice = data.cities.map((cityRelation) => ({
              value: cityRelation.city.id,
              label: cityRelation.city.name,
              state: cityRelation.city.state,
              country: cityRelation.city.country,
            }));
            setSelectedCities(citiesFromInvoice);
          }

          if (data.client) {
            setClient({
              value: data.client.id,
              label: data.client.name,
              client: data.client,
            });
          }

          if (data.driver) {
            setDriver({
              value: data.driver.id,
              label: `${data.driver.firstName} ${data.driver.lastName}`,
              driver: data.driver,
              car: data.driver.car || {
                plateNumber: data.driver.plateNumber,
                model: data.driver.carModel,
              },
            });
          }

          // Process items with shipping note data
          setInvoiceItems(
            data.items.map((item) => ({
              id: item.id,
              productId: item.productId,
              productName:
                item.product?.name ||
                item.shippingNoteItem?.product?.name ||
                `Produit ${item.productId}`,
              quantity: item.quantity,
              price: item.price,
              vatRate: item.vatRate,
              vatAmount: item.vatAmount,
              totalHT: (item.price || 0) * (item.quantity || 0),
              totalTTC:
                (item.price || 0) *
                (item.quantity || 0) *
                (1 + (item.vatRate || 0) / 100),
              // IMPORTANT: Get original quantity from shippingNoteItem
              originalQuantity:
                item.shippingNoteItem?.quantity || item.quantity,
              shippingNoteItemId: item.shippingNoteItemId || null,
              shippingNoteId:
                item.shippingNoteId || data.shippingNoteId || null,
              product: item.product,
              shippingNoteItem: item.shippingNoteItem,
            })),
          );

          // Set shipping note products if this invoice is linked to a shipping note
          if (data.shippingNote && data.shippingNote.items) {
            const shippingProducts = data.shippingNote.items.map((item) => ({
              productId: item.productId,
              productName: item.product?.name || `Produit ${item.productId}`,
              quantity: item.quantity,
              price: item.price,
              vatRate: item.vatRate,
              vatAmount: item.vatAmount,
              originalQuantity: item.quantity,
              shippingNoteItemId: item.id,
              shippingNoteId: data.shippingNote.id,
            }));
            setShippingNoteProducts(shippingProducts);

            // Set selected shipping note
            setSelectedShippingNote({
              value: data.shippingNote.id,
              label: data.shippingNote.invoiceNumber,
            });
          }

          console.log("Loaded invoice data with shipping note:", {
            shippingNoteId: data.shippingNoteId,
            items: data.items.map((i) => ({
              productId: i.productId,
              currentQty: i.quantity,
              originalQty: i.shippingNoteItem?.quantity,
              shippingNoteItemId: i.shippingNoteItemId,
            })),
          });
        } catch (error) {
          console.error("Error fetching invoice:", error);
          notify(2, "Erreur lors du chargement de la facture");
        }
      };
      fetchInvoice();
    }
  }, [id, dispatch, isUpdate]);

  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  return {
    // State
    date,
    setDate,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    invoiceItems,
    setInvoiceItems,
    invoiceNumber,
    setInvoiceNumber,
    products,
    setProducts,
    clients,
    setClients,
    drivers,
    setDrivers,
    cities,
    setCities,
    selectedCities,
    setSelectedCities,
    client,
    setClient,
    driver,
    setDriver,
    totalHT,
    setTotalHT,
    totalTTC,
    setTotalTTC,
    totalVAT,
    setTotalVAT,
    type,
    setType,
    status,
    setStatus,
    deliveryNotes,
    setDeliveryNotes,
    shippingNoteInvoices,
    setShippingNoteInvoices,
    selectedShippingNote,
    setSelectedShippingNote,
    shippingNoteProducts,
    setShippingNoteProducts,
    loadingDeliveryNotes,
    setLoadingDeliveryNotes,
    loadingShippingNotes,
    setLoadingShippingNotes,
    originalShippingNoteId,
    setOriginalShippingNoteId,
    invoice,
    setInvoice,

    // Auto-generation
    autoGenerateNumber,
    generatingNumber,

    // Utilities
    id,
    dispatch,
    navigate,
    calculateTotals,
    notify,
  };
};
